import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message, Alert } from 'antd';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import apiService from '@/services/api.service';
import { logger } from '@/utils/logger';
import { ResetPasswordContainer, StyledCard } from './styles/ResetPassword.styles';

const { Title, Text } = Typography;

const ResetPassword: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [email, setEmail] = useState<string>('');
    const [success, setSuccess] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setVerifying(false);
                setTokenValid(false);
                return;
            }

            try {
                const response: any = await apiService.get(`/auth/verify-reset-token?token=${token}`);
                if (response.data?.data?.valid) {
                    setTokenValid(true);
                    setEmail(response.data.data.email);
                } else {
                    setTokenValid(false);
                }
            } catch (error: any) {
                logger.error('Error verifying reset token:', error);
                setTokenValid(false);
            } finally {
                setVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (values: { password: string; confirmPassword: string }) => {
        if (values.password !== values.confirmPassword) {
            message.error('Пароли не совпадают');
            return;
        }

        try {
            setLoading(true);
            await apiService.post('/auth/reset-password', {
                token,
                password: values.password
            });
            setSuccess(true);
            message.success('Пароль успешно изменен');
            
            // Перенаправляем на страницу входа через 3 секунды
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (error: any) {
            logger.error('Error resetting password:', error);
            message.error(error.response?.data?.message || 'Не удалось изменить пароль');
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <ResetPasswordContainer>
                <StyledCard>
                    <div style={{ textAlign: 'center' }}>
                        <Title level={3}>Проверка токена...</Title>
                    </div>
                </StyledCard>
            </ResetPasswordContainer>
        );
    }

    if (!tokenValid) {
        return (
            <ResetPasswordContainer>
                <StyledCard>
                    <Alert
                        message="Недействительная ссылка"
                        description="Ссылка для восстановления пароля недействительна или истекла. Пожалуйста, запросите новую ссылку."
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <div style={{ textAlign: 'center' }}>
                        <Button type="primary" onClick={() => navigate('/forgot-password')}>
                            Запросить новую ссылку
                        </Button>
                    </div>
                </StyledCard>
            </ResetPasswordContainer>
        );
    }

    if (success) {
        return (
            <ResetPasswordContainer>
                <StyledCard>
                    <div style={{ textAlign: 'center' }}>
                        <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                        <Title level={3}>Пароль успешно изменен</Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                            Вы будете перенаправлены на страницу входа через несколько секунд...
                        </Text>
                        <Link to="/login">
                            <Button type="primary">Перейти к входу</Button>
                        </Link>
                    </div>
                </StyledCard>
            </ResetPasswordContainer>
        );
    }

    return (
        <ResetPasswordContainer>
            <StyledCard>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <Title level={2}>Новый пароль</Title>
                    <Text type="secondary">
                        Введите новый пароль для {email}
                    </Text>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    size="large"
                >
                    <Form.Item
                        name="password"
                        rules={[
                            { required: true, message: 'Введите новый пароль' },
                            { min: 8, message: 'Пароль должен содержать минимум 8 символов' }
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Новый пароль"
                            autoComplete="new-password"
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: 'Подтвердите пароль' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Пароли не совпадают'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Подтвердите пароль"
                            autoComplete="new-password"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            loading={loading}
                        >
                            Изменить пароль
                        </Button>
                    </Form.Item>
                </Form>
            </StyledCard>
        </ResetPasswordContainer>
    );
};

export default ResetPassword;

